<!-- Test-only page: text here is deliberately left untranslated (out of scope per #4317). -->
<cs-header>
  <cs-header-title>Test UI Components</cs-header-title>
</cs-header>

<cs-content>
  <cs-card-list>
    <!-- Index: every card and example row, read from the page after it renders. A link scrolls to its
         target and puts its id in the URL (scrollCard), so the address bar holds a direct link -->
    <cs-card data-card-id="index">
      <cs-card-header>Index</cs-card-header>
      <cs-card-content>
        <div class="showcase-index">
          @for (card of showcaseIndex; track card.id) {
            <div class="showcase-index-card">
              <a class="showcase-index-link card-link" role="link" tabindex="0" [attr.data-test]="'index-' + card.id"
                (click)="jumpToShowcase(card.id)" (keydown.enter)="jumpToShowcase(card.id)">{{ card.label }}</a>
              @for (row of card.rows; track row.id) {
                <a class="showcase-index-link" role="link" tabindex="0" [attr.data-test]="'index-' + row.id"
                  (click)="jumpToShowcase(row.id)" (keydown.enter)="jumpToShowcase(row.id)">{{ row.label }}</a>
              }
            </div>
          }
        </div>
      </cs-card-content>
    </cs-card>

    <!-- Each card below is a shell: its body lives in its own cs-test-*-card file beside this page.
         The cs-card stays here because cs-card-list lays out only its direct children, and cs-card
         takes its header and content by element name from this template -->
    <cs-card data-card-id="double-click">
      <cs-card-header>Double-Click Prevention Test</cs-card-header>
      <cs-card-content>
        <cs-test-double-click-card></cs-test-double-click-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="navigation">
      <cs-card-header>Component Test Pages</cs-card-header>
      <cs-card-content>
        <cs-test-navigation-card></cs-test-navigation-card>
      </cs-card-content>
    </cs-card>

    <!-- Showcase cards (#6020): every custom cs-* UI component that can be shown with literal sample
         data. A new cs-* component gets an example in the card of its kind, in that card's own
         cs-test-*-card file (design-docs/angular-components.md, "Creating New Components");
         scripts/ci/check-test-page-components.mjs enforces it. -->
    <cs-card data-card-id="control-outlines">
      <cs-card-header>Control Outlines</cs-card-header>
      <cs-card-content>
        <cs-test-control-outlines-card [(sampleText)]="sampleText" [(sampleDate)]="sampleDate"></cs-test-control-outlines-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="status-feedback">
      <cs-card-header>Status &amp; Feedback</cs-card-header>
      <cs-card-content>
        <cs-test-status-feedback-card></cs-test-status-feedback-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="layout">
      <cs-card-header>Layout</cs-card-header>
      <cs-card-content>
        <cs-test-layout-card [(sampleText)]="sampleText"></cs-test-layout-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="pickers">
      <cs-card-header>Pickers</cs-card-header>
      <cs-card-content>
        <cs-test-pickers-card></cs-test-pickers-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="content">
      <cs-card-header>Content</cs-card-header>
      <cs-card-content>
        <cs-test-content-card></cs-test-content-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="charts">
      <cs-card-header>Charts</cs-card-header>
      <cs-card-content>
        <cs-test-charts-card></cs-test-charts-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="scanner-parts">
      <cs-card-header>Scanner Parts</cs-card-header>
      <cs-card-content>
        <cs-test-scanner-parts-card></cs-test-scanner-parts-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="rest-timer">
      <cs-card-header>Rest Timer</cs-card-header>
      <cs-card-content>
        <cs-test-rest-timer-card></cs-test-rest-timer-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="input-measure">
      <cs-card-content>
        <cs-test-input-measure-card></cs-test-input-measure-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="alerts">
      <cs-card-header>Alert Tests</cs-card-header>
      <cs-card-content>
        <cs-test-alerts-card (annotatorRequested)="toggleAnnotator(true)"></cs-test-alerts-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="headers">
      <cs-card-header>Header Tests</cs-card-header>
      <cs-card-content>
        <cs-test-headers-card></cs-test-headers-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="tabs">
      <cs-card-header>Segment/Tab Tests</cs-card-header>
      <cs-card-content>
        <cs-test-tabs-card></cs-test-tabs-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="menus">
      <cs-card-header>Menu Tests</cs-card-header>
      <cs-card-content>
        <cs-test-menus-card></cs-test-menus-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="colors">
      <cs-card-header>Colors</cs-card-header>
      <cs-card-content class="color-test">
        <cs-test-colors-card></cs-test-colors-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="calendar">
      <cs-card-content>
        <cs-test-calendar-card [(sampleDate)]="sampleDate"></cs-test-calendar-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="checkbox">
      <cs-card-content>
        <cs-test-checkbox-card></cs-test-checkbox-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="toggle">
      <cs-card-content>
        <cs-test-toggle-card></cs-test-toggle-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="radio">
      <cs-card-content>
        <cs-test-radio-card></cs-test-radio-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="loading-texts">
      <cs-card-header data-test="loading-texts-header">Loading texts</cs-card-header>
      <cs-card-content>
        <cs-test-loading-texts-card></cs-test-loading-texts-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="loading">
      <cs-card-header>Loading Component Tests</cs-card-header>
      <cs-card-content>
        <cs-test-loading-card></cs-test-loading-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="hint-bubble">
      <cs-card-header>Hint Bubble Component Tests</cs-card-header>
      <cs-card-content>
        <cs-test-hint-bubble-card></cs-test-hint-bubble-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="tooltip">
      <cs-card-header>Tooltip Component Test</cs-card-header>
      <cs-card-content>
        <cs-test-tooltip-card></cs-test-tooltip-card>
      </cs-card-content>
    </cs-card>

    <cs-card data-card-id="table">
      <cs-card-header>Table Component Test</cs-card-header>
      <cs-card-content>
        <cs-test-table-card></cs-test-table-card>
      </cs-card-content>
    </cs-card>
  </cs-card-list>

<!-- the app version line; it shows once the page is scrolled to the bottom -->
<cs-version-display></cs-version-display>

</cs-content>

<!-- cs-screenshot-annotator lays itself out as a whole page, so it opens over this page -->
@if (showAnnotator) {
  <div class="showcase-annotator-overlay">
    <cs-screenshot-annotator #annotator [imageDataUrl]="annotatorImage" (closeRequested)="toggleAnnotator(false)"></cs-screenshot-annotator>
  </div>
}